import exclamationIcon from "@assets/images/exclamation.svg";
import {
  Box,
  Button,
  Dialog,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  withStyles
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { makeStyles } from "@saleor/macaw-ui";
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState
} from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

const useStyle = makeStyles(
  () => ({
    dialog: {
      width: "448px",
      height: "auto",
      minHeight: "auto",
      maxHeight: "604px",
      padding: "32px 24px 32px 24px",
      borderRadius: "16px",
      boxShadow: "none !important",
      background: "#ffffff"
    },
    title: {
      fontSize: "20px",
      fontWeight: 700,
      color: "#222222",
      textAlign: "center"
    },
    recordCount: {
      fontSize: "16px",
      padding: "0px 0px 0px 0px",
      textAlign: "center",
      color: "#222222",
      "& span": {
        fontWeight: "bold"
      }
    },
    iconCenter: {
      textAlign: "center"
    },
    info: {
      fontSize: "14px",
      fontWeight: 700,
      color: "#222222"
    },
    button: {
      alignSelf: "center",
      background:
        "linear-gradient(90deg, #601863 0%, #681661 12%, #81125C 32%, #A90C55 57%, #DF044A 86%, #FD0045 100%)",
      borderRadius: "4px",
      fontSize: "18px",
      width: "100%",
      height: "40px",
      padding: "8px 0px"
    },
    buttonTitle: {
      fontSize: "18px",
      color: "#FFFFFF"
    },
    table: {
      maxWidth: "400px",
      marginTop: "8px",
      marginBottom: "32px",
      border: "1px solid #E0E0E0",
      borderRadius: "4px",
      boxShadow: "none",
      "& .MuiTableRow-root": {
        maxHeight: "40px",

        "&:nth-child(even)": {
          background: "#fff",
          maxHeight: "40px",
          "&:hover": {
            backgroundColor: "#fff !important"
          }
        },
        "&:hover:not(.MuiTableRow-head):not(.MuiTableRow-footer)": {
          backgroundColor: "#white !important",
          position: "relative"
        },
        "&:before": {
          display: "none"
        },
        "& td": {
          height: "40px !important",
          borderBottom: "1px solid #E0E0E0"
        },
        "& th": {
          height: "40px !important",
          borderBottom: "1px solid #E0E0E0"
        },
        "&:last-child": {
          "& td": {
            borderBottom: "none"
          },
          "& th": {
            borderBottom: "none"
          }
        }
      }
    }
  }),
  { name: "DialogErrorLine" }
);

export interface IRefDialog {
  onOpen: (errors: any) => void;
  visible: boolean;
  onClose: () => void;
}

export interface IError {
  field: string;
  message: string;
}

const DialogErrorLine: ForwardRefRenderFunction<IRefDialog> = (_props, ref) => {
  const classes = useStyle();
  const intl = useIntl();

  const [visible, setVisible] = useState<boolean>(false);
  const [errors, setErrors] = useState<null | IError[]>([]);

  const onClose = () => setVisible(false);

  const onOpen = (e: IError[]) => {
    setErrors(e);
    setVisible(true);
  };

  useImperativeHandle(ref, () => ({ onOpen, onClose, visible }));

  const StyledTableHead = withStyles(
    () => ({
      root: {
        "& .MuiTableCell-head": {
          backgroundColor: "#FFF2F6",
          height: "44px"
        }
      }
    }),
    { name: "StyledTableHead" }
  )(TableHead);

  const table = (
    <TableContainer component={Paper} className={classes.table}>
      <Table>
        <StyledTableHead>
          <TableRow>
            <TableCell>Line</TableCell>
            <TableCell align="left">Remarks</TableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {errors.length > 0 &&
            errors.map((row, index) => (
              <TableRow key={index}>
                <TableCell component="th" scope="row">
                  {row.field}
                </TableCell>
                <TableCell align="left">{row.message}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog
      open={visible}
      keepMounted
      onClose={onClose}
      style={{
        borderRadius: "16px",
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.25)"
      }}
      PaperProps={{
        style: { borderRadius: 16 }
      }}
    >
      <Box className={classes.dialog} display="flex" flexDirection={"column"}>
        <Box className={classes.iconCenter}>
          {" "}
          <SVG src={exclamationIcon} />
        </Box>
        <MyTypography variant="h6" className={classes.title}>
          {intl.formatMessage({
            defaultMessage: "อัปโหลดไฟล์ไม่สำเร็จ",
            description: "error"
          })}
        </MyTypography>
        <Typography className={classes.recordCount}>
          {intl.formatMessage({
            defaultMessage: `เนื่องจากเกิดข้อมูลผิดพลาด `,
            description: "records"
          })}
          <span>{errors.length}</span> records
        </Typography>
        <Typography className={classes.info}>
          {intl.formatMessage({
            defaultMessage: "รายละเอียด",
            description: "info"
          })}
        </Typography>

        {table}
        <Button className={classes.button} onClick={onClose}>
          <Typography className={classes.buttonTitle}>
            {intl.formatMessage({
              defaultMessage: "ปิดหน้าต่าง",
              description: "close"
            })}
          </Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default forwardRef(DialogErrorLine);
